import React, { useState, useContext, useEffect, JSX } from "react";
import { useRouter } from "next/router";
import { UserContext } from "context/user";
import { CartContext } from "context/cart";
import { AppContext } from "context/app";
import { useScreenSize } from "@pepdirect/v3/hooks/useScreenSize";
import {
  NavHeader as PepNavHeader,
  CartProps,
  LogoProps,
  NavLinksProps,
  UserProps,
  SearchBarProps,
} from "@pepdirect/v3/ui/NavHeader";
import { AlgoliaSearch } from "@pepdirect/v3/ui/AlgoliaSearch";
import { convertFromCentsToDollars } from "@pepdirect/v3/helpers/currency";
import {
  useAdvancedVariantSearchLazyQuery,
  AdvancedVariantSearchQuery,
} from "services/graphql/generated";

import { endpoints, headerPromotion } from "config";

type SearchVariant = NonNullable<
  NonNullable<AdvancedVariantSearchQuery["advancedVariantSearch"]>[number]
>["variant"];

export function NavHeader(): JSX.Element {
  const router = useRouter();
  const { currentUserId, logout } = useContext(UserContext);
  const { getSize } = useContext(CartContext);
  const { tenant } = useContext(AppContext);

  const size = getSize();
  const { isMobile } = useScreenSize();

  const [headerKey, setHeaderKey] = useState("h");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchVariant[]>([]);

  // Search query hook
  const [
    executeSearch,
    { data: searchQueryData, loading: searchLoading, error: searchError },
  ] = useAdvancedVariantSearchLazyQuery();

  // Update search results when data changes
  useEffect(() => {
    if (searchQueryData?.advancedVariantSearch) {
      const variants = searchQueryData.advancedVariantSearch.flatMap((result) =>
        result?.variant ? [result.variant] : []
      );
      setSearchResults(variants);
    }
  }, [searchQueryData]);

  const logoutAndResetHeader = async () => {
    if (isMobile) {
      // HACK to reset header, should be removed later
      setHeaderKey(`${headerKey}-1`);
    }
    await logout();
    router.push("/sign-in");
  };

  const tenantAllowsAlgoliaSearch =
    tenant?.userExperience?.algoliaSearchActive || false;

  const searchData: SearchBarProps = {
    showSearchBar: tenantAllowsAlgoliaSearch,
    placeholderText: ``,
    onSearchClicked: () => setIsSearchOpen(!isSearchOpen),
  };

  const userData: UserProps = {
    accountUrl: `${endpoints.portalFrontend}/v3/dashboard/account/`,
    hideIcon: true,
    isLoggedIn: !!currentUserId,
    onLogOutClicked: logoutAndResetHeader,
  };

  const logo: LogoProps = {
    logoImgLink: `${endpoints.cdn}/static-images/gatorade-icons/teamzone-logo.png`,
    logoUrl: `/`,
  };

  const navLinks: NavLinksProps = {
    navItems: userData.isLoggedIn
      ? [
          {
            title: `Shop`,
            link: `/collections/shop-all`,
            id: "shop",
          },
          {
            title: `Orders`,
            link: `${endpoints.portalFrontend}/v3/dashboard/orders`,
            id: "orders",
          },
        ]
      : [],
  };

  const cart: CartProps = {
    numOfItems: size,
    cartUrl: `/cart`,
  };

  return (
    <div>
      <PepNavHeader
        authUsersOnly
        accountAsNavLink
        key={headerKey}
        logo={logo}
        navLinks={navLinks}
        promotion={headerPromotion}
        searchBar={searchData}
        cart={cart}
        user={userData}
      />

      {isSearchOpen && currentUserId && (
        <AlgoliaSearch
          onCloseSearchClicked={() => {
            setIsSearchOpen(false);
            setSearchResults([]);
          }}
          onSearch={(searchTerm: string) => {
            executeSearch({
              variables: { term: searchTerm },
            });
          }}
          results={searchResults}
          loading={searchLoading}
          error={searchError?.message || null}
          formatPrice={(price) => convertFromCentsToDollars(price)}
        />
      )}
    </div>
  );
}
